import { get } from 'lodash';

import {
  BUSINESS_STRUCTURE,
  EMPLOYMENT_EMPLOYMENT_TYPE,
  EMPLOYMENT_TYPE,
  FIELD_PATHS,
  INCOME_TYPE,
} from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { Company, EmploymentEntityIndex, Income } from '@funnels/types';
import { FunnelGrid, FunnelGridUnit } from '@funnels/ui-components/Common';
import { EmploymentType } from '@funnels/ui-components/Employment';
import { BusinessIncomeType } from '@funnels/ui-components/Income';
import {
  BusinessName,
  BusinessOwnership,
  BusinessStructure,
  CompanySelector,
} from '@funnels/ui-components/RelatedCompany';

import { BusinessFullDocIncome } from './BusinessFullDocIncome';
import { BusinessLowDocIncome } from './BusinessLowDocIncome';
import { EmploymentIncome } from './EmploymentIncome';

interface EmploymentSituationProps extends EmploymentEntityIndex {
  applicantId: string;
}

export const EmploymentSituation = (props: EmploymentSituationProps) => {
  const { employmentIndex, applicantId } = props;
  const [formValues] = useFormValues();
  const companies: Company[] = get(formValues, FIELD_PATHS.COMPANIES(), []);
  const incomes: Income[] = get(formValues, FIELD_PATHS.INCOMES(), []);
  const employmentStatus = get(formValues, FIELD_PATHS.EMPLOYMENT_TYPE(employmentIndex));
  const employmentType = get(formValues, FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_TYPE(employmentIndex));
  const companyClientId = get(formValues, FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(employmentIndex));
  const companyIndex = companies.findIndex(
    (company) => get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0)) === companyClientId
  );
  const businessStructure = get(formValues, FIELD_PATHS.COMPANY_BUSINESS_STRUCTURE(companyIndex));
  const companyIncomeIndex = incomes.findIndex(
    (income) => get({ incomes: [income] }, FIELD_PATHS.INCOME_COMPANY_CLIENT_ID(0)) === companyClientId
  );
  const businessIncomeType = get(formValues, FIELD_PATHS.INCOME_TYPE(companyIncomeIndex));
  const isSecondaryRole = employmentStatus === EMPLOYMENT_TYPE.SECONDARY;
  const isSelfEmployed = employmentType === EMPLOYMENT_EMPLOYMENT_TYPE.SELF_EMPLOYED;
  const isEmployee = employmentType === EMPLOYMENT_EMPLOYMENT_TYPE.EMPLOYEE;

  return (
    <FunnelGrid>
      <FunnelGridUnit mb="md">
        <EmploymentType employmentIndex={employmentIndex} shouldHideNotWorkingOption={isSecondaryRole} />
      </FunnelGridUnit>
      {isSelfEmployed && (
        <CompanySelector employmentIndex={employmentIndex} companyIndex={companyIndex} isInCustomerView />
      )}
      {isSelfEmployed && (
        <FunnelGridUnit mb="md">
          <BusinessStructure companyIndex={companyIndex} employmentIndex={employmentIndex} isOptional />
        </FunnelGridUnit>
      )}
      {isSelfEmployed && (
        <FunnelGridUnit mb="md">
          <BusinessOwnership
            companyIndex={companyIndex}
            applicantId={applicantId}
            isDisabled={businessStructure === BUSINESS_STRUCTURE.SOLE_TRADER}
            isOptional
          />
        </FunnelGridUnit>
      )}
      {companyIndex > -1 && (
        <FunnelGridUnit mb="md">
          <BusinessName companyIndex={companyIndex} label={isSelfEmployed ? 'Business name' : 'Employer name'} />
        </FunnelGridUnit>
      )}
      {isSelfEmployed && (
        <FunnelGridUnit mb="md">
          <BusinessIncomeType companyIndex={companyIndex} isOptional />
        </FunnelGridUnit>
      )}
      {isSelfEmployed && businessIncomeType === INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_FULL_DOC && (
        <BusinessFullDocIncome incomeIndex={companyIncomeIndex} />
      )}
      {isSelfEmployed && businessIncomeType === INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_LOW_DOC && (
        <BusinessLowDocIncome incomeIndex={companyIncomeIndex} />
      )}
      {isEmployee && <EmploymentIncome employmentIndex={employmentIndex} />}
    </FunnelGrid>
  );
};
